import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { Download } from "lucide-react";
import type { Prescription } from "@clinic/types";
import { api } from "@/lib/api";
import { openPrescriptionDoc } from "@/lib/prescriptionDoc";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { SkeletonRows } from "@/components/ui/skeleton";
import { Table, TBody, Td, Th, THead, Tr } from "@/components/ui/table";

export function PrescriptionsPage() {
  const prescriptions = useQuery({
    queryKey: ["prescriptions"],
    queryFn: () => api.prescriptions.list(),
  });
  const clinic = useQuery({ queryKey: ["clinic"], queryFn: () => api.settings.getClinic() });

  /** Real Rx have a server-generated PDF (pdfUrl); otherwise render one client-side. */
  const download = (rx: Prescription) => {
    if (rx.pdfUrl && !rx.pdfUrl.startsWith("#")) {
      window.open(rx.pdfUrl, "_blank");
      return;
    }
    if (rx.eyeRecord) {
      openPrescriptionDoc({
        clinic: clinic.data ?? null,
        patientName: rx.patient?.fullName ?? "Patient",
        idNumber: rx.patient?.idNumber,
        dob: rx.patient?.dob,
        phone: rx.patient?.phone,
        record: rx.eyeRecord,
        doctorName: rx.doctor?.name ?? "Doctor",
        prescriptionId: rx.id,
        issuedAt: rx.issuedAt,
      });
    } else {
      window.open(rx.pdfUrl || "#", "_blank");
    }
  };

  return (
    <div>
      <PageHeader
        title="Prescriptions"
        subtitle="Issued from a patient's exam record (History tab → Issue prescription PDF)"
      />
      {prescriptions.isLoading ? (
        <SkeletonRows rows={6} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>Rx ID</Th>
              <Th>Patient</Th>
              <Th>Issued</Th>
              <Th>Doctor</Th>
              <Th className="w-24">PDF</Th>
            </Tr>
          </THead>
          <TBody>
            {(prescriptions.data ?? []).map((rx) => (
              <Tr key={rx.id}>
                <Td className="font-mono text-xs text-text-secondary">{rx.id.slice(-8)}</Td>
                <Td>
                  <Link
                    to="/patients/$patientId"
                    params={{ patientId: rx.patientId }}
                    className="font-medium hover:text-accent-light"
                  >
                    {rx.patient?.fullName}
                  </Link>
                </Td>
                <Td>{format(new Date(rx.issuedAt), "d MMM yyyy, HH:mm")}</Td>
                <Td>{rx.doctor?.name}</Td>
                <Td>
                  <Button variant="ghost" size="sm" onClick={() => download(rx)}>
                    <Download size={14} /> Download PDF
                  </Button>
                </Td>
              </Tr>
            ))}
            {(prescriptions.data ?? []).length === 0 && (
              <Tr>
                <Td colSpan={5} className="py-10 text-center text-text-tertiary">
                  No prescriptions issued yet.
                </Td>
              </Tr>
            )}
          </TBody>
        </Table>
      )}
    </div>
  );
}
